import type { ComponentChildren } from "preact";
import { useRef, useState } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import { COULD_NOT_SAVE_ERROR, EDITED_CHIP_LABEL, EDIT_BUTTON_LABEL } from "../lib/recordsCopy";

import { ConfirmDialog } from "./ConfirmDialog";

export interface RecordArchiveConfirmCopy {
  title: string;
  body: string;
  confirmLabel: string;
}

export interface RecordDetailOverlayProps {
  /** Whether the overlay is open. Every current call site mounts this
   * component conditionally (so it's always `true` when rendered); kept as
   * a prop for parity with the `useModalA11y` contract and defaulting to
   * `true`. */
  active?: boolean;
  /** The real close callback (returns to the list). Routed through the
   * shared `useModalA11y` close path — Escape, the ×/Back button, and
   * hardware/browser Back all converge here (TD-012). */
  onClose: () => void;
  /** Record name — rendered as the header `<h1>`. */
  title: string;
  /** `<section>` `aria-label`. Defaults to `"{title} details"`; Person
   * overrides it (first-name possessive) via this prop. */
  ariaLabel?: string;
  /** Back-button `aria-label` (e.g. `"Back to Projects"`). */
  backLabel: string;
  /** Gates the shared Edit / Archive / Restore chrome. A surface that owns
   * none of these (Person — its edit is an inline body control) passes
   * `false`. */
  isAdmin: boolean;
  /** Drives the "Archived" tag + the Archive↔Restore button swap. */
  isArchived: boolean;
  /** `isEditedRecord(created_at, updated_at)` — surfaces the "Edited"
   * chip. Surfaces with no timestamps (Contact) pass `false`. */
  showEditedChip: boolean;
  /** Per-surface "Archived" tag copy (`recordsCopy.ts`). */
  archivedTagLabel?: string;
  /** Opens the surface's own `*FormSheet` (rendered by the consumer inside
   * `children`). When omitted, no header Edit button is drawn. */
  onEdit?: () => void;
  /** Archive submit (threaded from the consumer's `archive*`). Shown as
   * the shared bottom Archive button behind the shared `ConfirmDialog`. */
  onArchive?: () => Promise<void>;
  /** Restore submit — no confirm (reversible/non-destructive). */
  onRestore?: () => Promise<void>;
  /** Copy for the shared Archive `ConfirmDialog`. Required when
   * `onArchive` is provided. */
  archiveConfirm?: RecordArchiveConfirmCopy;
  /** Shared Archive/Restore button label overrides (default "Archive" /
   * "Restore"). */
  archiveButtonLabel?: string;
  restoreButtonLabel?: string;
  /** Surfaces the generic save-error snackbar if an Archive/Restore
   * submit throws (mirrors the pre-extraction per-overlay behavior). */
  onNotify?: (message: string) => void;
  /** Sprint-017 E2 (TD-039) — opt-in dirty-state guard, forwarded straight
   * to `useModalA11y`. A consumer with unsaved edits (Person's inline name)
   * returns `false` to intercept Escape + hardware/browser Back + the Back
   * button and show a discard-confirm, WITHOUT wedging the modal stack. */
  dirtyGuard?: () => boolean | Promise<boolean>;
  /** THE BODY SLOT — per-surface content (summary + tasks, contractor
   * fields, passkey admin, …) plus the surface's own `*FormSheet` and any
   * surface-specific confirm dialogs. */
  children: ComponentChildren;
}

/**
 * Sprint-017 E1 (TD-038, ADR-003 §7.2) — the shared CRUD detail-overlay
 * chrome, extracted from the previously-divergent Project / Contact /
 * Person detail overlays (AnimalDetailOverlay's tabbed/photo body is
 * deferred to a fast-follow per the SA's 3-of-4 phasing). It owns:
 *
 * - the `<section role="dialog" aria-modal>` shell + `useModalA11y` wiring
 *   (focus trap / restore / Escape / hardware-Back / the E2 `dirtyGuard`),
 * - the header (Back button + title),
 * - the top chrome row (Archived tag + Edited chip + Edit affordance),
 * - the bottom Archive↔Restore control + the Archive `ConfirmDialog`.
 *
 * Everything a surface does differently is a prop or the `children` body
 * slot — the shared component owns no body-specific state. Each surface
 * routes its Edit affordance to its own `*FormSheet` (rendered inside
 * `children`), so this component never imports a surface-specific sheet.
 */
export function RecordDetailOverlay({
  active = true,
  onClose,
  title,
  ariaLabel,
  backLabel,
  isAdmin,
  isArchived,
  showEditedChip,
  archivedTagLabel,
  onEdit,
  onArchive,
  onRestore,
  archiveConfirm,
  archiveButtonLabel = "Archive",
  restoreButtonLabel = "Restore",
  onNotify,
  dirtyGuard,
  children,
}: RecordDetailOverlayProps) {
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const containerRef = useRef<HTMLElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);

  const { close } = useModalA11y(containerRef, {
    active,
    onClose,
    initialFocusRef: backButtonRef,
    guard: dirtyGuard,
  });

  async function handleConfirmArchive() {
    setArchiving(true);
    setConfirmArchive(false);
    try {
      // Success unmounts this whole overlay (the parent's `onArchive`
      // closes it and shows the snackbar).
      await onArchive?.();
    } catch {
      setArchiving(false);
      onNotify?.(COULD_NOT_SAVE_ERROR);
    }
  }

  // No `ConfirmDialog` — restoring is reversible/non-destructive.
  async function handleRestore() {
    setRestoring(true);
    try {
      await onRestore?.();
    } catch {
      setRestoring(false);
      onNotify?.(COULD_NOT_SAVE_ERROR);
    }
  }

  const showTopChrome = isArchived || showEditedChip || (isAdmin && onEdit != null);
  // Gated on `isAdmin` alone — the exact pre-extraction Project/Contact
  // behavior: the Archive/Restore control shows for any admin, the handler
  // itself is optional (`onArchive?()` / `onRestore?()`). A surface with no
  // archive lifecycle (Person) passes `isAdmin={false}`.
  const showArchiveRestore = isAdmin;

  return (
    <section
      ref={containerRef}
      class="overlay-view is-open"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? `${title} details`}
    >
      <div class="overlay-header">
        <button
          ref={backButtonRef}
          type="button"
          class="icon-btn"
          aria-label={backLabel}
          onClick={close}
        >
          ‹
        </button>
        <h1 class="step-title">{title}</h1>
        <span style={{ width: "48px" }} />
      </div>

      <div class="overlay-body" tabIndex={0}>
        {showTopChrome ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {/* Read-visible to every role that can already view the record
             * (same posture as the "Edited" chip). */}
            {isArchived && archivedTagLabel ? (
              <span class="admin-tag is-archived">{archivedTagLabel}</span>
            ) : null}
            {showEditedChip ? <span class="edited-chip">{EDITED_CHIP_LABEL}</span> : null}
            {isAdmin && onEdit ? (
              <button
                type="button"
                class="secondary-btn"
                style={{ width: "auto", flexShrink: 0, marginLeft: "auto" }}
                onClick={onEdit}
              >
                {EDIT_BUTTON_LABEL}
              </button>
            ) : null}
          </div>
        ) : null}

        {children}

        {showArchiveRestore ? (
          isArchived ? (
            <button
              type="button"
              class="secondary-btn"
              style={{ marginTop: "24px" }}
              disabled={restoring}
              onClick={() => void handleRestore()}
            >
              {restoring ? "Restoring…" : restoreButtonLabel}
            </button>
          ) : (
            <button
              type="button"
              class="secondary-btn"
              style={{ marginTop: "24px" }}
              disabled={archiving}
              onClick={() => setConfirmArchive(true)}
            >
              {archiving ? "Archiving…" : archiveButtonLabel}
            </button>
          )
        ) : null}
      </div>

      {isAdmin && archiveConfirm ? (
        <ConfirmDialog
          open={confirmArchive}
          title={archiveConfirm.title}
          body={archiveConfirm.body}
          confirmLabel={archiveConfirm.confirmLabel}
          destructive
          onConfirm={() => void handleConfirmArchive()}
          onCancel={() => setConfirmArchive(false)}
        />
      ) : null}
    </section>
  );
}
